<template>
  <li v-if="megaMenuOptions?.enabled">
    <button @click="openMegaMenu"
            class="link h-full flex gap-x-1 items-center whitespace-nowrap btn-link hover:cursor-pointer"
            ref="megaMenuButton"
            aria-haspopup="menu"
            :aria-label="`Open ${label} menu`"
            :aria-expanded="megaMenuActive"
            :aria-controls="megaMenuId"
    >
      {{ label }}
      <div v-if="tag" class="rounded-full text-sm text-center px-3 py-1 text-white"
          :style="linkTagStyling">
        {{tag}}
      </div>
    </button>
    <Transition enter-from-class="opacity-0" 
                enter-to-class="opacity-100"
                enter-active-class="transition-opacity ease-[ease] duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
                leave-active-class="transition-opacity ease-[ease] duration-200"
    >
      <div v-show="megaMenuActive"
           :id="megaMenuId"
           ref="megaMenu"
           class="absolute left-0 top-0 w-full bg-white"
           :aria-hidden="!megaMenuActive"
           @focusout="megaMenuLoseFocus"
      >
        <button @click="closeMegaMenu"
                :aria-label="`Close ${label} menu`"
                class="text-gray-700 hover:cursor-pointer"
        >
          <Icon icon="angle-left" />
          Back
        </button>

        <hr class="mt-2 mb-6 text-gray-300">

        <div class="flex flex-col sm:flex-row sm:justify-between">
          <ul class="flex flex-col gap-6 mb-6">
            <li v-for="(column, columnIndex) in megaMenuOptions.columns"
                :id="megaMenuColumnId(columnIndex)"
                :data-index="columnIndex + 1"
                class="flex flex-col"
            >
              <h3 class="mb-6 text-xl text-black">
                <NuxtLink v-if="column.headerUrl" 
                   :href="column.headerUrl" 
                   class="hover:text-black!"
                   external
                >
                  {{ column.header }}
                </NuxtLink>
                <template v-else>
                   {{ column.header }}
                </template>
              </h3>
              <ul class="grid grid-flow-col auto-cols-max grid-rows-[repeat(13,auto)] gap-[0_5rem]">
                <li v-for="(link) in column.links"
                    class="max-w-[20ch] wrap-break-word mb-2">
                  <NuxtLink :to="link.url" 
                            class="text-lg text-ash-100 hover:text-black!"
                            external
                  >
                    {{ link.label }}
                  </NuxtLink>
                </li>
              </ul>
            </li>
          </ul>

          <div v-if="!!megaMenuOptions?.featured"
              :id="megaMenuColumnId('featured')"
              :key="`mega-col-mobile-featured-${megaMenuId}`"
              :data-index="megaMenuOptions.columns.length"
              class="flex flex-col max-w-1/2 sm:max-w-1/3"
          >
            <h3 class="mb-6 text-xl text-black">{{ megaMenuOptions.featured.headline }}</h3>
            <img :src="megaMenuOptions.featured.imageUrl" :alt="megaMenuOptions.featured.imageAltText">
            <div class="mt-3 mb-5">
              <h4 class="text-xl text-black">{{ megaMenuOptions.featured.description.headline }}</h4>
              <p class="text-ash-100">{{ megaMenuOptions.featured.description.copy }}</p>
            </div>
            <a v-if="!!megaMenuOptions.featured.callToAction"
                :href="megaMenuOptions.featured.callToAction.url"
                class="inline-flex items-center min-h-14 w-fit text-xl text-white hover:text-white! bg-ash-500 hover:bg-ash-100 rounded-md px-[1em]"
            >
              {{ megaMenuOptions.featured.callToAction.label }}
            </a>
          </div>
        </div>
      </div>
    </Transition>
  </li>
</template>

<script setup lang="ts">

import Icon from '../atom/icon.vue';
import { type BuilderNavLink } from '../organism/navBar.vue';

const {
  label,
  tag = '',
  tagTextColor = 'white',
  tagBackgroundColor = 'none',
  megaMenuOptions,
} = defineProps<BuilderNavLink>();

const megaMenu = ref();
const megaMenuButton = ref();
const megaMenuActive = ref(false);
const megaMenuCleanLinkLabel = computed(() => label?.replaceAll(/[^a-zA-Z0-9]/g, '') ?? '');
const megaMenuId = computed(() => `mega-menu-mobile-${megaMenuCleanLinkLabel.value}`);
const megaMenuColumnId = (index) => `mega-menu-mobile-column-${megaMenuCleanLinkLabel.value}-${index}`;

const openMegaMenu = () => {
  megaMenuActive.value = true;
}

const closeMegaMenu = () => {
  megaMenuActive.value = false;
}

const megaMenuLoseFocus = (event: FocusEvent) => {
  if (!event.relatedTarget || megaMenu.value.contains(event.relatedTarget)) {
    return;
  }

  megaMenuButton.value.focus();
  megaMenuActive.value = false;
}

const linkTagStyling = computed(() => ({
  color: tagTextColor || 'white', 
  backgroundColor: tagBackgroundColor || 'none'
}));

defineExpose({
  openMegaMenu,
  closeMegaMenu
});

</script>